import { Pause, Play } from "lucide-react";
import {
    type PointerEvent as ReactPointerEvent,
    useEffect,
    useEffectEvent,
    useRef,
    useState,
} from "react";
import { Pressable } from "../../../Pressable";
import { Label } from "../../../typography/Label";
import { useChat } from "../../ChatContext";
import { useMessage } from "../Context";
import type { ChatAttachment } from "../types";

const WAVE_PATH =
    "M118 2.64844C115.838 0.450259 112.295 0.450257 110.133 2.64844C107.972 4.84662 104.428 4.84662 102.267 2.64844C100.105 0.450257 96.5615 0.450259 94.4 2.64844C92.2385 4.84662 88.6949 4.84662 86.5333 2.64844C84.3718 0.450257 80.8282 0.450258 78.6667 2.64844C76.5051 4.84662 72.9615 4.84662 70.8 2.64844C68.6385 0.450258 65.0949 0.450257 62.9333 2.64844C60.7718 4.84662 57.2282 4.84662 55.0667 2.64844C52.9051 0.450258 49.3615 0.450258 47.2 2.64844C45.0385 4.84662 41.4949 4.84662 39.3333 2.64844C37.1718 0.450258 33.6282 0.450257 31.4667 2.64844C29.3051 4.84662 25.7615 4.84662 23.6 2.64844C21.4385 0.450258 17.8949 0.450258 15.7333 2.64844C13.5718 4.84662 10.0282 4.84662 7.86667 2.64844C5.70512 0.450257 2.16154 0.450257 0 2.64844";

const WAVE_TILE_WIDTH = 119;
const WAVE_TILE_HEIGHT = 6;
const THUMB_SIZE_PX = 16;

type AudioPlayerProps = {
    attachment: ChatAttachment;
};

type IosWindow = Window & { isIosNativeWebView?: boolean };

function isIosAudioDevice(): boolean {
    if (typeof navigator === "undefined") {
        return false;
    }

    if ((window as IosWindow).isIosNativeWebView) {
        return true;
    }

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        return true;
    }

    // iPadOS reports a desktop Mac user agent.
    return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

export function AudioPlayer({ attachment }: AudioPlayerProps) {
    const { sender } = useMessage();
    const { labels } = useChat();
    const audioRef = useRef<HTMLAudioElement>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const unplayedLineRef = useRef<HTMLDivElement>(null);
    const waveClipRef = useRef<HTMLDivElement>(null);
    const thumbRef = useRef<HTMLDivElement>(null);
    const durationRef = useRef(0);
    const trackWidthRef = useRef(0);
    const isDraggingRef = useRef(false);
    const downloadedUrlRef = useRef<string | undefined>(undefined);
    const preparingPlaybackRef = useRef(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [trackWidth, setTrackWidth] = useState(0);

    const remainingMs = Math.max(0, duration - currentTime) * 1000;
    const waveWidth = trackWidth || WAVE_TILE_WIDTH;
    const waveTileCount = Math.max(1, Math.ceil(waveWidth / WAVE_TILE_WIDTH));
    const thumbHoleClass =
        sender === "member" ? "bg-component-chat-member" : "bg-component-chat-agent";
    const downloadsOnPlay =
        isIosAudioDevice() && !!attachment.url && !attachment.url.startsWith("blob:");

    const paintProgress = useEffectEvent((time: number) => {
        const progress =
            durationRef.current > 0 ? Math.min(time / durationRef.current, 1) : 0;
        const thumbCenterX =
            progress * Math.max(trackWidthRef.current - THUMB_SIZE_PX, 0) +
            THUMB_SIZE_PX / 2;

        if (unplayedLineRef.current) {
            unplayedLineRef.current.style.left = `${thumbCenterX}px`;
        }
        if (waveClipRef.current) {
            waveClipRef.current.style.width = `${thumbCenterX}px`;
        }
        if (thumbRef.current) {
            thumbRef.current.style.transform = `translate3d(${thumbCenterX}px, -50%, 0) translateX(-50%)`;
        }
    });

    const syncDuration = useEffectEvent(() => {
        const audio = audioRef.current;
        if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) {
            return;
        }

        durationRef.current = audio.duration;
        setDuration(audio.duration);
        paintProgress(audio.currentTime);
    });

    useEffect(function observeTrackWidth() {
        const track = trackRef.current;
        if (!track) {
            return;
        }

        function updateTrackWidth(width: number) {
            trackWidthRef.current = width;
            setTrackWidth(width);
            paintProgress(audioRef.current?.currentTime ?? 0);
        }

        const observer = new ResizeObserver(([entry]) => {
            if (entry) {
                updateTrackWidth(entry.contentRect.width);
            }
        });
        observer.observe(track);
        updateTrackWidth(track.clientWidth);

        return function disconnectTrackObserver() {
            observer.disconnect();
        };
    }, []);

    useEffect(
        function paintProgressFromCurrentTime() {
            paintProgress(currentTime);
        },
        [currentTime],
    );

    useEffect(
        function syncPlaybackWhilePlaying() {
            if (!isPlaying) {
                return;
            }

            let frameId = 0;
            let lastLabeledSecond = -1;

            function syncPlaybackPosition() {
                const audio = audioRef.current;
                if (audio && !isDraggingRef.current) {
                    paintProgress(audio.currentTime);
                    const labeledSecond = Math.floor(audio.currentTime);
                    if (labeledSecond !== lastLabeledSecond) {
                        lastLabeledSecond = labeledSecond;
                        setCurrentTime(audio.currentTime);
                    }
                }
                frameId = requestAnimationFrame(syncPlaybackPosition);
            }

            frameId = requestAnimationFrame(syncPlaybackPosition);
            return function cancelPlaybackSync() {
                cancelAnimationFrame(frameId);
            };
        },
        [isPlaying],
    );

    useEffect(
        function revokeDownloadedAudio() {
            const source = attachment.url;
            return () => {
                if (!source) {
                    return;
                }
                const objectUrl = downloadedUrlRef.current;
                if (!objectUrl) {
                    return;
                }
                URL.revokeObjectURL(objectUrl);
                downloadedUrlRef.current = undefined;
            };
        },
        [attachment.url],
    );

    if (!attachment.url) {
        return null;
    }

    async function togglePlayback() {
        const audio = audioRef.current;
        if (!audio) {
            return;
        }

        if (!audio.paused) {
            audio.pause();
            return;
        }

        if (preparingPlaybackRef.current) {
            return;
        }

        preparingPlaybackRef.current = true;
        try {
            const sourceUrl = attachment.url;
            if (downloadsOnPlay && sourceUrl && !downloadedUrlRef.current) {
                const objectUrl = await downloadPlayableAudio(sourceUrl);
                downloadedUrlRef.current = objectUrl;
                audio.src = objectUrl;
            }
            await audio.play();
        } catch {
            setIsPlaying(false);
        } finally {
            preparingPlaybackRef.current = false;
        }
    }

    function seekToClientX(clientX: number) {
        const audio = audioRef.current;
        const track = trackRef.current;
        const total = durationRef.current;
        if (!audio || !track || total <= 0) {
            return;
        }

        const rect = track.getBoundingClientRect();
        const usableWidth = Math.max(rect.width - THUMB_SIZE_PX, 0);
        if (usableWidth <= 0) {
            return;
        }

        const ratio = Math.min(
            1,
            Math.max(0, (clientX - rect.left - THUMB_SIZE_PX / 2) / usableWidth),
        );
        audio.currentTime = ratio * total;
        setCurrentTime(audio.currentTime);
        paintProgress(audio.currentTime);
    }

    function handleTrackPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
        event.stopPropagation();
        event.preventDefault();
        isDraggingRef.current = true;
        event.currentTarget.setPointerCapture(event.pointerId);
        seekToClientX(event.clientX);
    }

    function handleTrackPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
        if (!isDraggingRef.current) {
            return;
        }

        event.stopPropagation();
        seekToClientX(event.clientX);
    }

    function handleTrackPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
        if (!isDraggingRef.current) {
            return;
        }

        event.stopPropagation();
        isDraggingRef.current = false;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    }

    return (
        <div className="flex w-[220px] max-w-full flex-row items-center gap-12">
            <audio
                ref={audioRef}
                preload={downloadsOnPlay ? "none" : "metadata"}
                src={downloadsOnPlay ? undefined : attachment.url}
                className="hidden"
                onLoadedMetadata={syncDuration}
                onDurationChange={syncDuration}
                onPlay={() => setIsPlaying(true)}
                onPause={() => {
                    setIsPlaying(false);
                    setCurrentTime(audioRef.current?.currentTime ?? 0);
                }}
                onEnded={() => {
                    setIsPlaying(false);
                    setCurrentTime(durationRef.current);
                }}
            >
                <track kind="captions" srcLang="en" label="Audio message" />
            </audio>

            <Pressable
                aria-label={isPlaying ? labels.pauseVoice : labels.playVoice}
                className="shrink-0"
                onPress={() => {
                    void togglePlayback();
                }}
            >
                {isPlaying ? (
                    <Pause aria-hidden="true" className="size-24 text-text-primary" />
                ) : (
                    <Play aria-hidden="true" className="size-24 text-text-primary" />
                )}
            </Pressable>

            <div
                ref={trackRef}
                role="slider"
                tabIndex={0}
                aria-label={labels.voiceProgress}
                aria-valuemin={0}
                aria-valuemax={Math.round(duration)}
                aria-valuenow={Math.round(currentTime)}
                aria-valuetext={formatAudioTime(remainingMs)}
                className="relative flex h-24 min-w-0 flex-1 cursor-pointer touch-none items-center"
                onPointerDown={handleTrackPointerDown}
                onPointerMove={handleTrackPointerMove}
                onPointerUp={handleTrackPointerUp}
                onPointerCancel={handleTrackPointerUp}
                onKeyDown={(event) => {
                    const audio = audioRef.current;
                    const total = durationRef.current;
                    if (!audio || total <= 0) {
                        return;
                    }

                    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
                        event.preventDefault();
                        const delta = event.key === "ArrowLeft" ? -1 : 1;
                        audio.currentTime = Math.min(
                            total,
                            Math.max(0, audio.currentTime + delta),
                        );
                        setCurrentTime(audio.currentTime);
                        paintProgress(audio.currentTime);
                    }
                }}
            >
                <div
                    ref={unplayedLineRef}
                    className="absolute top-1/2 right-0 h-2 -translate-y-1/2 rounded-full bg-text-tertiary"
                />

                <div
                    ref={waveClipRef}
                    className="absolute inset-y-0 left-0 overflow-hidden"
                >
                    <svg
                        aria-hidden="true"
                        width={waveWidth}
                        height={WAVE_TILE_HEIGHT}
                        viewBox={`0 0 ${waveWidth} ${WAVE_TILE_HEIGHT}`}
                        fill="none"
                        className="absolute top-1/2 left-0 -translate-y-1/2 text-text-tertiary"
                        shapeRendering="geometricPrecision"
                    >
                        {Array.from({ length: waveTileCount }, (_, tile) => {
                            const offsetX = tile * WAVE_TILE_WIDTH;
                            return (
                                <path
                                    key={offsetX}
                                    d={WAVE_PATH}
                                    transform={`translate(${offsetX} 0)`}
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                />
                            );
                        })}
                    </svg>
                </div>

                <div
                    ref={thumbRef}
                    className="absolute top-1/2 left-0 z-10 flex size-16 items-center justify-center rounded-full bg-component-notification-background"
                >
                    <div className={`size-8 rounded-full ${thumbHoleClass}`} />
                </div>
            </div>

            <Label size="small" weight="bold" className="shrink-0 tabular-nums">
                {formatAudioTime(remainingMs)}
            </Label>
        </div>
    );
}

async function downloadPlayableAudio(source: string): Promise<string> {
    const response = await fetch(source);
    if (!response.ok) {
        throw new Error("Could not download the voice message");
    }

    const downloaded = await response.blob();
    const playable = downloaded.type.startsWith("audio/")
        ? downloaded
        : new Blob([downloaded], { type: "audio/mp4" });
    return URL.createObjectURL(playable);
}

function formatAudioTime(elapsedMs: number): string {
    const totalSeconds = Math.floor(elapsedMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}
